/** @format */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { NavLink, Link, pathPrefixer } from 'fe-appdir-comp-link';
import { withRouter } from 'react-router-dom';
import { connect } from 'fe-hoc-connect';
import { compose } from 'fe-hoc-compose';
import { store, toggleIsUserSearching } from 'fe-appdir-data-app';
import theme, { rems } from 'fe-appdir-lib-theme';
import { boxSizing } from 'fe-appdir-lib-utils';
import { ButtonLink } from 'fe-appdir-comp-button';
import { MobileNav } from 'fe-appdir-comp-mobile-nav';
import SearchBar from 'fe-appdir-comp-search-bar';
import AppLogo from './Logo';
import AppNav from './Nav';
import AppButtons from './Buttons';

const ACTIVE_NAV_CLASSNAME = 'is-active';
const HEADER_Z_INDEX = 5;

const PROP_TYPES = {
  isLoggedIn: PropTypes.bool,
  history: PropTypes.object,
  gaTrackIds: PropTypes.shape({
    NavTrackIds: PropTypes.object,
    SearchTrackIds: PropTypes.object,
  }),
  logCustomEvent: PropTypes.func,
};

const DEFAULT_PROPS = {
  gaTrackIds: {
    NavTrackIds: {},
    SearchTrackIds: {},
  },
};

const Header = styled.header`
  ${boxSizing(true)};
  position: fixed;
  top: 0;
  width: 100%;
  z-index: ${HEADER_Z_INDEX};
  height: ${theme.headerHeight};
  background-color: ${theme.colorWhite};
  border-bottom: 1px solid ${theme.colorBorder};
  font-size: ${theme.fontSizeBase};
  font-family: ${theme.fontFamilyPrimary};

  @media (max-width: ${theme.breakpointMobileNav}) {
    background-color: ${theme.colorGrey98};
    height: ${theme.headerHeightMobile};
  }

  @supports (position: sticky) {
    position: sticky;
  }
`;

// Pushes content down for browsers that don't support position: sticky
const HeaderClear = styled.div`
  display: block;
  z-index: -1;
  visibility: hidden;
  height: ${theme.headerHeight};

  @media (max-width: ${theme.breakpointMobileNav}) {
    height: ${theme.headerHeightMobile};
  }

  @supports (position: sticky) {
    display: none;
  }
`;

const Container = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: stretch;
  margin: 0 auto;
  max-width: 970px;
  width: 100%;
  padding: 0 15px;
`;

const LogoLink = styled(Link)`
  flex-shrink: 1;
  margin: ${theme.spacingLg};
  margin-left: 0;
  margin-right: ${theme.spacingMd};

  @media (max-width: ${theme.breakpointMobileNav}) {
    margin: ${rems(18)} auto;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  margin: auto ${theme.spacingMd};
  z-index: ${HEADER_Z_INDEX + 1};

  @media (max-width: ${theme.breakpointSm}) {
    margin: auto ${theme.spacingSm};
    display: flex;
    justify-content: flex-end;
  }
`;

const Nav = styled(AppNav)`
  margin-left: auto;

  @media (max-width: ${theme.breakpointMobileNav}) {
    display: none;
  }
`;

const Auth = styled(AppButtons)`
  @media (max-width: ${theme.breakpointMobileNav}) {
    display: none;
  }
`;

// Hide SearchResults dropdown when clicking on one result
const onSearchResultClick = () => toggleIsUserSearching(false);

const StatelessAppHeader = ({
  isLoggedIn,
  history,
  gaTrackIds,
  logCustomEvent,
}) => {
  // Redirect to the search results page and hide search dropdown
  const onSearchSubmit = (event) => {
    event.preventDefault();
    history.push(pathPrefixer('/search')); // TODO: update to proper page when decided
    toggleIsUserSearching(false);
  };

  const { NavTrackIds, SearchTrackIds } = gaTrackIds;

  return (
    <React.Fragment>
      <Header className="header">
        <Container>
          <LogoLink to="/" gaTrackId={NavTrackIds.NAV_HOME}>
            <AppLogo />
          </LogoLink>
          <SearchWrapper>
            <SearchBar
              onSubmit={onSearchSubmit}
              onResultClick={onSearchResultClick}
              gaTrackIds={SearchTrackIds}
              logCustomEvent={logCustomEvent}
            />
          </SearchWrapper>
          <Nav
            links={[
              <NavLink
                to="/apps"
                activeClassName={ACTIVE_NAV_CLASSNAME}
                gaTrackId={NavTrackIds.NAV_APPS}
                exact
              >
                Apps
              </NavLink>,
              <NavLink
                to="/categories"
                activeClassName={ACTIVE_NAV_CLASSNAME}
                gaTrackId={NavTrackIds.NAV_CATEGORIES}
              >
                Categories
              </NavLink>,
            ]}
          />
          <Auth
            links={[
              isLoggedIn ? (
                <ButtonLink
                  href="https://hootsuite.com/login"
                  size="sm"
                  gaTrackId={NavTrackIds.NAV_DASHBOARD}
                >
                  Dashboard
                </ButtonLink>
              ) : (
                <ButtonLink
                  href="https://hootsuite.com/plans"
                  size="sm"
                  gaTrackId={NavTrackIds.NAV_SIGN_UP}
                >
                  Sign Up
                </ButtonLink>
              ),
              <ButtonLink
                href="https://hootsuite.com/dashboard"
                size="sm"
                type="standard"
                gaTrackId={NavTrackIds.NAV_LOG_IN}
              >
                Log In
              </ButtonLink>,
            ]}
          />
          <MobileNav
            authButtons={
              isLoggedIn
                ? [
                    {
                      type: 'default',
                      to: '/',
                      label: 'App Directory',
                      gaTrackId: NavTrackIds.NAV_HOME,
                    },
                    {
                      type: 'standard',
                      href: 'https://hootsuite.com/logout',
                      label: 'Log Out',
                      gaTrackId: NavTrackIds.NAV_LOG_OUT,
                    },
                  ]
                : [
                    {
                      type: 'default',
                      href: 'https://hootsuite.com/plans',
                      label: 'Sign Up',
                      gaTrackId: NavTrackIds.NAV_SIGN_UP,
                    },
                    {
                      type: 'standard',
                      href: 'https://hootsuite.com/login',
                      label: 'Log In',
                      gaTrackId: NavTrackIds.NAV_LOG_IN,
                    },
                  ]
            }
            navLinks={[
              { label: 'Apps', to: '/apps', gaTrackId: NavTrackIds.NAV_APPS },
              {
                label: 'Categories',
                to: '/categories',
                gaTrackId: NavTrackIds.NAV_CATEGORIES,
              },
            ]}
          />
        </Container>
      </Header>
      <HeaderClear />
    </React.Fragment>
  );
};

StatelessAppHeader.propTypes = PROP_TYPES;
StatelessAppHeader.defaultProps = DEFAULT_PROPS;

const AppHeader = compose(
  withRouter,
  connect(store, (state) => ({
    isLoggedIn: state.isLoggedIn,
  })),
)(StatelessAppHeader);

export default AppHeader;
